import React, { useEffect, useState, useCallback } from "react";
import { Flex } from "@fluentui/react-northstar";
import { useRequireAuth } from "../../hooks/use-require-auth";
import CallAssistantPageTemplate from "../../components/templates/CallAssistantPageTemplate";
import { useMst } from "../../hooks/useMst";
import { observer } from "mobx-react-lite";
import {
  setRegistrationStatus,
  setTeamRegistrationStatus,
  getStatusDropdownItems,
  setStatusCode,
  setReasonCode,
  setRegisteredNumber,
  setLocationCode,
  setCustomValue,
  getShowWelcomeDialog,
} from "./controller";
import SignalR from "../../services/SignalR/signalR";
import CustomValues from "../../constants/custom-values";
import { useHistory, useLocation } from "react-router-dom";


const CallAssistant = observer((props) => {
  const auth = useRequireAuth(); // use this if this page requires the user to be logged in. They will be redirected to login if not
  let loc = useLocation();
  let history = useHistory();

  let firstname = "";
  if (loc.state) {
    firstname = loc.state.firstname;
  }

  

  const [statusDropdownItems, setStatusDropdownItems] = useState([]);
  const [currentCallTeamId, setCurrentCallTeamId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  const {
    currentUser,
    permissionStore,
    reasonCodeStore,
    statusCodeStore,
    locationStore,
    agentStore,
  } = useMst();

  // If we get in to call-assitant without having been through the initialisation of the stores
  // redirect back to login page
  if(!currentUser.agent) {
    history.replace({ pathname: "/login" });
    return; 
  }

  const setCurrentCallTeam = (id) => {
    setCurrentCallTeamId(id);
  };

  const checkShowWelcomeDialog = useCallback(async () => {
    const shouldShow = await getShowWelcomeDialog(
      CustomValues.CV_SKIP_WELCOME,
      permissionStore
    );

    if (shouldShow) {
      setShowWelcomeDialog(true);
    }
  }, [permissionStore]);

  useEffect(() => {
    checkShowWelcomeDialog();
  }, [checkShowWelcomeDialog]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      const items = getStatusDropdownItems(
        statusCodeStore.statusCodes,
        reasonCodeStore.reasonCodes,
        currentUser.agent.loggedInTeams.length > 0
      );
      setStatusDropdownItems(items);
    }
  }, [auth.isAuthenticated, statusCodeStore, reasonCodeStore, currentUser.agent.loggedInTeams]);

  const handleLogout = () => {
    // Disconnect SignalR before signout
    SignalR.stop();

    auth.signout();
  };

  const handleSettingsClicked = (val) => {
    setShowSettings(val);
  };

  const setRegisteredHandler = (value) => {
    if (currentUser.agent.registered === value) return;
    setRegistrationStatus(value)
      .then(() => {
        currentUser.agent.setRegistered(value);
      })
      .catch((err) => {
        console.warn(err);
      });
  };

  const setTeamRegistration = (id, status) => {
    setTeamRegistrationStatus(id, !status, permissionStore)
      .then(() => {
        currentUser.agent.updateTeamStatus(id, !status);
      })
      .catch((err) => {
        console.warn(err);
      });
  };

  const setAgentReasonCode = (reasonCode) => {
    if (reasonCode && reasonCode !== "") {
      setReasonCode(reasonCode)
        .then(() => {
          currentUser.agent.setReasonCode(reasonCode);
        })
        .catch((err) => {
          console.warn(err);
        });
      return;
    }
    currentUser.agent.setReasonCode(null);
  };

  const setStatus = (statusCode, reasonCode) => {
    if (
      !permissionStore.havePermissions("Call Manager", [
        "PutStatus",
        "PutReason",
      ])
    )
      return;
    // if ready, set registration to true if unregistered
    if (statusCode === 1 && !currentUser.agent.registered) {
      setRegisteredHandler(true);
    }

    setStatusCode(statusCode)
      .then(() => {
        currentUser.agent.setStatusCode(statusCode);
        setAgentReasonCode(reasonCode);
      })
      .catch((err) => {
        console.warn(err);
      });
  };

  const setLocation = (location) => {
    // call out to api, depending upon setting a saved location or a temporary number
    if (location.code) {
      // Saved location
      setLocationCode(location.code).catch((err) => {
        console.warn(err);
      });
      return;
    }
    if (location.phoneNumber) {
      setRegisteredNumber(location.phoneNumber).catch((err) => {
        console.warn(err);
      });
    }
  };

  const welcomeDialogComplete = () => {
    setShowWelcomeDialog(false);
    setCustomValue(CustomValues.CV_SKIP_WELCOME, "true", permissionStore);
  };

  const renderTemplate = (isAuthenticated) => {
    let template = null;

    if (isAuthenticated) {
      template = (
        <CallAssistantPageTemplate
          signOutClicked={handleLogout}
          settingsClicked={handleSettingsClicked}
          currentUser={currentUser}
          statusDropdownItems={statusDropdownItems}
          locations={locationStore.orderedLocations}
          setRegistered={setRegisteredHandler}
          setTeamRegistration={setTeamRegistration}
          setStatus={setStatus}
          setLocation={setLocation}
          currentCallTeamId={currentCallTeamId}
          setCurrentCallTeam={setCurrentCallTeam}
          agents={agentStore.agents}
          showSettings={showSettings}
          showWelcomeDialog={showWelcomeDialog}
          welcomeDialogComplete={welcomeDialogComplete}
          firstname={firstname}
        />
      );
    }

    return template;
  };

  return <Flex className="page">{renderTemplate(auth.isAuthenticated)}</Flex>;
});

export default CallAssistant;
